import React, { useState } from "react";
import { overviewLinks, navGroups, TLink, TNavGroup } from "./adminLinks";
// mui
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { UserState } from "../../types/types";

const LinkRow = ({
  link,
  onNavigate,
  indent,
}: {
  link: TLink;
  onNavigate?: () => void;
  indent: boolean;
}) => {
  const location = useLocation();
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to={link.path}
        selected={location.pathname === link.path}
        onClick={onNavigate}
        sx={indent ? { pl: 4 } : undefined}
      >
        <ListItemText primary={link.title} />
      </ListItemButton>
    </ListItem>
  );
};

const NavGroupItem = ({
  group,
  visibleLinks,
  onNavigate,
}: {
  group: TNavGroup;
  visibleLinks: TLink[];
  onNavigate?: () => void;
}) => {
  const location = useLocation();
  const hasActiveLink = visibleLinks.some(
    (link) => location.pathname === link.path
  );
  const [open, setOpen] = useState(hasActiveLink);

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton onClick={() => setOpen(!open)} selected={hasActiveLink}>
          <ListItemIcon
            sx={{ fontSize: "22px", minWidth: "32px", color: "inherit" }}
          >
            {<group.icon />}
          </ListItemIcon>
          <ListItemText primary={group.title} />
          {open ? <MdExpandLess /> : <MdExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {visibleLinks.map((link) => (
            <LinkRow key={link.path} link={link} onNavigate={onNavigate} indent />
          ))}
        </List>
      </Collapse>
    </>
  );
};

const LinksDrawer = ({ onNavigate }: { onNavigate?: () => void }) => {
  const userData = useSelector((state: UserState) => state.user.userData);
  const userRoles = userData.roles;

  const shouldRenderLink = (link: TLink) => {
    // Check if the link has a userHas property
    if (link.userHas) {
      // If userData doesn't have the property or value doesn't match, don't render
      if (!link.userHas || userData[link.userHas as keyof typeof userData] == true) {
        return true;
      }
    }

    // Check role-based permissions as before
    return (
      (link.allowedTo.includes(0) ||
        userRoles.some((role: number) => link.allowedTo.includes(role))) &&
      !link.userHas
    );
  };

  const visibleOverviewLinks = overviewLinks.filter(shouldRenderLink);

  return (
    <div>
      <List sx={{ paddingBlock: 0 }}>
        {visibleOverviewLinks.map((link) => (
          <LinkRow
            key={link.path}
            link={link}
            onNavigate={onNavigate}
            indent={false}
          />
        ))}
        {navGroups.map((group) => {
          const visibleLinks = group.links.filter(shouldRenderLink);
          if (visibleLinks.length === 0) return null;
          return (
            <NavGroupItem
              key={group.key}
              group={group}
              visibleLinks={visibleLinks}
              onNavigate={onNavigate}
            />
          );
        })}
      </List>
    </div>
  );
};

export default LinksDrawer;
